/**
 * ChatContext — shared AI chat state for the floating ChatWidget and the
 * full-page AIChatPage, so both surfaces show the same conversation.
 *
 * Also lets any page/component programmatically open the ChatWidget and
 * optionally pre-fill a message, or navigate to the Support panel with a
 * specific tutorial highlighted.
 *
 * Usage (chat):
 *   const { openChatWithMessage } = useChatContext();
 *   openChatWithMessage("Walk me through building a strategy.");
 *
 * Usage (tutorial highlight):
 *   const { pendingTutorial, clearPendingTutorial } = useChatContext();
 *   // PageHelpBubble navigates to /chat with location.state = { tab: "tutorials", tutorialId: "06" }
 *   // ChatPage reads location.state directly — no context needed for routing.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

// Runtime white-label config — set by nginx-mounted /app-config.js
const appName = (window.APP_CONFIG || {}).appName || "Fincrest Systems";

let _msgId = 0;
const nextId = () => `m${++_msgId}`;

function makeGreeting() {
  return {
    id: nextId(),
    role: "bot",
    content:
      `Hi! I'm ${appName} AI — your platform support assistant.\n\n` +
      "I can help you understand strategies, build rules, navigate the platform, " +
      "and troubleshoot issues. What would you like to know?",
    ts: new Date(),
  };
}

const ChatContext = createContext({
  openChatWithMessage: () => {},
  pendingMessage: null,
  clearPendingMessage: () => {},
  messages: [],
  loading: false,
  historyReady: false,
  sessionId: null,
  sendMessage: () => {},
  startNewChat: () => {},
});

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [pendingMessage, setPendingMessage] = useState(null);
  const [messages, setMessages]     = useState([makeGreeting()]);
  const [loading, setLoading]       = useState(false);
  const [historyReady, setHistoryReady] = useState(false);
  const sessionIdRef = useRef(null);

  /* On every mount, pull the user's entire message history (merged across
     all of their past sessions) from the backend and resume appending new
     messages to whichever session owns the most recent one. This is the
     source of truth — no client-side session pointer is cached, so a stray
     empty/test session can never shadow real history. */
  useEffect(() => {
    if (!user?.username) return undefined;
    let cancelled = false;
    setHistoryReady(false);

    const startFresh = () => (
      axios.post("/chat/session", { username: user.username })
        .then(res => {
          if (cancelled) return;
          sessionIdRef.current = res.data.session_id;
          setMessages([makeGreeting()]);
        })
        .catch(() => {
          sessionIdRef.current = `local-${Date.now()}`;
          setMessages([makeGreeting()]);
        })
    );

    axios.get("/chat/history")
      .then(res => {
        if (cancelled) return;
        const { messages: rows, active_session_id } = res.data || {};
        if (!rows || rows.length === 0 || !active_session_id) {
          return startFresh();
        }
        sessionIdRef.current = active_session_id;
        setMessages(rows.map(r => ({
          id:            nextId(),
          role:          (r.role === "user" || r.role === "system") ? r.role : "bot",
          content:       r.content,
          ts:            r.created_at,
          escalated:     !!r.escalated,
          confidence:    r.confidence,
          modelUsed:     r.model_used,
          cacheEntryId:  r.cache_entry_id,
          costUsd:       r.cost_usd,
        })));
      })
      .catch(() => startFresh())
      .finally(() => { if (!cancelled) setHistoryReady(true); });

    return () => { cancelled = true; };
  }, [user?.username]);

  const openChatWithMessage = useCallback((text) => {
    setPendingMessage(text);
  }, []);

  const clearPendingMessage = useCallback(() => {
    setPendingMessage(null);
  }, []);

  const sendMessage = useCallback(async (text, { accountUsername } = {}) => {
    const userMsg = { id: nextId(), role: "user", content: text, ts: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await axios.post("/chat/message", {
        session_id:       sessionIdRef.current,
        message:          text,
        username:         user?.username || "anonymous",
        account_username: accountUsername || "",
      });

      const data = res.data;
      setMessages(prev => [...prev, {
        id:           nextId(),
        role:         "bot",
        content:      data.reply,
        ts:           new Date(),
        escalated:    data.escalated,
        confidence:   data.confidence,
        modelUsed:    data.model_used,
        cacheEntryId: data.cache_entry_id,
        costUsd:      data.cost_usd,
      }]);

      if (data.token_warning) {
        setMessages(prev => [...prev, {
          id: nextId(), role: "system",
          content: "⚠️ This conversation is approaching its message limit. Consider starting a new chat soon.",
          ts: new Date(),
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: nextId(), role: "bot",
        content: err.response?.data?.reply || "⚠️ Something went wrong. Please try again in a moment.",
        ts: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const startNewChat = useCallback(() => {
    const username = user?.username || "anonymous";
    axios.post("/chat/session", { username })
      .then(res => {
        sessionIdRef.current = res.data.session_id;
        setMessages([makeGreeting()]);
      })
      .catch(() => {
        sessionIdRef.current = `local-${Date.now()}`;
        setMessages([makeGreeting()]);
      });
  }, [user]);

  return (
    <ChatContext.Provider value={{
      openChatWithMessage,
      pendingMessage,
      clearPendingMessage,
      messages,
      loading,
      historyReady,
      sessionId: sessionIdRef.current,
      sendMessage,
      startNewChat,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  return useContext(ChatContext);
}
